import * as viemChains from 'viem/chains'
import { getWalletStrategy } from '@shared/wallet'
import { custom, extractChain, createWalletClient } from 'viem'
import {
  route,
  chains,
  assets,
  balances,
  executeRoute,
  // messagesDirect,
  transactionStatus
} from '@skip-go/client'
import { IS_TESTNET } from '@/app/utils/constants'
import { peggyTokenToSkipAsset } from '@/app/utils/skip'
import { TOKEN_REMAP } from '@/app/utils/skip/token-remap'
import type {
  Chain,
  Asset,
  UserAddress,
  RouteRequest,
  RouteResponse,
  BalanceRequest,
  BalanceResponse
} from '@skip-go/client'
import type {
  SkipTransaction,
  UiSkipTransaction,
  SkipAssetWithBalance
} from '@/types/skip'

type SkipStoreState = {
  chains: Chain[]
  assets: Record<string, Asset[]>
  route: undefined | RouteResponse
  transactions: UiSkipTransaction[]
  rawTransactions: SkipTransaction[]
  chainBalances: BalanceResponse['chains']
}

const initialStateFactory = (): SkipStoreState => ({
  chains: [],
  assets: {},
  chainBalances: {},
  route: undefined,
  transactions: [],
  rawTransactions: []
})

export const useSkipStore = defineStore('skip', {
  state: () => initialStateFactory(),
  getters: {
    chainAssetsWithTokenBalances: (state) => {
      const chainAssetsWithTokenBalances: Record<
        string,
        SkipAssetWithBalance[]
      > = {}

      for (const chain of state.chains) {
        const chainId = chain.chainId

        const chainAssets = state.assets[chainId] || []
        const chainBalances = state.chainBalances?.[chainId]

        const chainBalancesDenoms = chainBalances?.denoms || {}

        chainAssetsWithTokenBalances[chainId] = chainAssets.map((asset) => {
          const balance = chainBalancesDenoms[asset.denom]

          return {
            asset,
            balance,
            isSkipAsset: true
          }
        })
      }

      return chainAssetsWithTokenBalances
    },

    peggyAssetsOnEthereum: () => {
      const tokenStore = useTokenStore()

      return (
        tokenStore.ethereumNetworkTokens.map((token) =>
          peggyTokenToSkipAsset(token, true)
        ) || []
      )
    },

    peggyAssetsOnInjective: () => {
      const tokenStore = useTokenStore()

      return tokenStore.ethereumNetworkTokens.map((token) =>
        peggyTokenToSkipAsset(token, false)
      )
    }
  },

  actions: {
    async fetchChains() {
      const skipChains = await chains({
        includeEvm: true,
        includeSvm: false, // No support for Solana
        onlyTestnets: IS_TESTNET
      })

      this.chains = skipChains || []
    },

    async fetchAssets(chainIds?: string[]) {
      const skipAssets =
        (await assets({
          includeEvmAssets: true,
          includeSvmAssets: false,
          chainIds,
          onlyTestnets: IS_TESTNET
        })) || {}

      for (const [chainId, remappedAssets] of Object.entries(TOKEN_REMAP)) {
        if (!skipAssets[chainId]) {
          return
        }

        skipAssets[chainId] = skipAssets[chainId].map((skipAsset) => {
          if (remappedAssets[skipAsset.denom]) {
            const remappedAsset = remappedAssets[skipAsset.denom]

            return {
              ...skipAsset,
              ...remappedAsset
            }
          }

          return skipAsset
        })
      }

      this.assets = skipAssets
    },

    async fetchBalances(chains: BalanceRequest['chains']) {
      const skipBalances = await balances({ chains })

      this.chainBalances = skipBalances?.chains || {}
    },

    async fetchRoute(routeRequest: RouteRequest) {
      this.route = await route(routeRequest)
    },

    async executeRoute({
      userAddresses,
      route
    }: {
      route: RouteResponse
      userAddresses: UserAddress[]
    }) {
      const skipStore = useSkipStore()

      return new Promise((resolve, reject) => {
        executeRoute({
          route: route,
          userAddresses,
          getCosmosSigner: async (chainId: string) => {
            const walletStrategy = await getWalletStrategy()
            const strategy = walletStrategy.getStrategy()

            if (!strategy.getOfflineSigner) {
              throw new Error('Not implemented')
            }

            const offlineSigner = await strategy.getOfflineSigner(chainId)

            return offlineSigner as any
          },
          getEvmSigner: async (chainID) => {
            const walletStrategy = await getWalletStrategy()
            const strategy = walletStrategy.getStrategy()
            if (!strategy) {
              throw new Error('Strategy not found')
            }

            let provider: any

            if (strategy.getEip1193Provider) {
              provider = await strategy.getEip1193Provider()
            }

            if (!provider) {
              throw new Error('Provider not found')
            }

            const chain = extractChain({
              chains: Object.values(viemChains),
              id: parseInt(chainID) as any
            })

            const account = (await provider.request({
              method: 'eth_requestAccounts'
            })) as string[] | undefined

            if (!account) throw new Error('Account not found')

            const evmWalletClient = createWalletClient({
              account: account[0] as `0x${string}`,
              chain: chain,
              transport: custom(provider)
            })

            await evmWalletClient.switchChain({ id: chainID as any })

            return evmWalletClient as any
          },
          getSvmSigner: async (): Promise<any> => {
            throw new Error('Not implemented')
          },

          onTransactionBroadcast: async (txInfo) => {
            skipStore.addTransaction({
              chainId: txInfo.chainId,
              txHash: txInfo.txHash,
              unixTimestamp: Date.now(),
              inputAmount: route.amountIn,
              inputDenom: route.sourceAssetDenom,
              outputAmount: route.amountOut,
              outputDenom: route.destAssetDenom,
              inputChainId: route.sourceAssetChainId,
              outputChainId: route.destAssetChainId,
              sourceAddress: userAddresses[0]?.address || '',
              destinationAddress:
                userAddresses[userAddresses.length - 1]?.address || ''
            })

            resolve(txInfo)
          }
        }).catch((error) => {
          if (error instanceof Error) {
            reject(error)
          } else {
            reject(new Error('Unknown error'))
          }
        })
      })
    },

    async fetchTransactions() {
      const skipStore = useSkipStore()
      const sharedWalletStore = useSharedWalletStore()

      const userAddress = [
        sharedWalletStore.address.toLowerCase(),
        sharedWalletStore.injectiveAddress.toLowerCase()
      ]

      const transactions = await Promise.all(
        skipStore.rawTransactions
          .filter((tx) => {
            return (
              userAddress.includes(tx.destinationAddress.toLowerCase()) ||
              userAddress.includes(tx.sourceAddress.toLowerCase())
            )
          })
          .map(async (transaction) => {
            const txData = await skipStore.transactionStatus({
              txHash: transaction.txHash,
              chainId: transaction.chainId
            })

            return {
              ...transaction,
              txData
            }
          })
      )

      skipStore.$patch({
        transactions: transactions
      })
    },

    async transactionStatus({
      txHash,
      chainId
    }: {
      txHash: string
      chainId: string
    }) {
      const skipTransactionStatus = await transactionStatus({ txHash, chainId })

      return skipTransactionStatus
    },

    async addTransaction(transaction: SkipTransaction) {
      const skipStore = useSkipStore()

      skipStore.$patch({
        rawTransactions: [...skipStore.rawTransactions, transaction]
      })
    }
  }
})
